import React, { useState, useRef, useEffect } from "react";
import { BellIcon, ArrowPathIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { useSOC } from "../context/SOCContext";
import CRMModuleHeader from "../../ui/CRMModuleHeader";
import { Button } from "../../ui";

const CATEGORY_DOT = {
  "Critical Alert": "bg-red-500",
  "High Alert": "bg-orange-500",
  "Medium Alert": "bg-yellow-500",
  "Low Alert": "bg-blue-500",
  "Hourly Security Summary": "bg-purple-500",
  "Scan Completed": "bg-green-500",
  "Scan Failed": "bg-red-500",
};

const ScanIcon = ({ className = "" }) => (
  <ArrowPathIcon className={`${className} animate-spin text-blue-600`} />
);

const Header = ({ onNavigate }) => {
  const { notifications, unreadCount, markNotificationRead, markAllNotificationsRead, scan, runScanNow } = useSOC();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleClick = (n) => {
    markNotificationRead(n.id);
    setOpen(false);
    if (n.link) onNavigate(n.link.tab, n.link.id);
  };

  return (
    <CRMModuleHeader
      icon={ShieldExclamationIcon}
      title="AI Virtual SOC"
      description="Real-time monitoring & response (demo data)"
      rightContent={(
        <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="secondary"
          size="md"
          icon={scan.status === "Running" ? ScanIcon : ArrowPathIcon}
          onClick={runScanNow}
          disabled={scan.status === "Running"}
        >
          {scan.status === "Running" ? "Scanning..." : "Run Scan Now"}
        </Button>

        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen((o) => !o)}
            className="relative p-2.5 rounded-lg bg-white shadow border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <BellIcon className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-96 max-h-[28rem] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl z-50">
              <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllNotificationsRead} className="text-xs text-blue-600 hover:underline">
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No notifications yet.</p>
              ) : (
                notifications.slice(0, 30).map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 flex gap-2 ${
                      !n.read ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${CATEGORY_DOT[n.category] || "bg-gray-400"}`} />
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold text-gray-700">{n.category}</span>
                      <span className="block text-xs text-gray-500 whitespace-pre-line line-clamp-4">{n.message}</span>
                      <span className="block text-[10px] text-gray-400 mt-1">{new Date(n.time).toLocaleTimeString()}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        </div>
      )}
    />
  );
};

export default Header;
